import type { ThemeConfig } from "@/types/theme";

export const tailwindStone: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-stone-700 dark:text-stone-400",
    secondary: "text-stone-900 dark:text-stone-500",
    accent: "text-blue-600 dark:text-blue-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-stone-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-stone-600 dark:bg-stone-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-stone-50 to-gray-100 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-stone-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-stone-600 dark:bg-stone-500",
    backgroundSecondaryColor: "bg-stone-800 dark:bg-stone-700",
    backgroundAccentColor: "bg-blue-500 dark:bg-blue-400",
    backgroundPrimarySubtle: "bg-stone-50 dark:bg-stone-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-stone-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-stone-700 dark:text-stone-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-stone-600 dark:text-stone-400",
    iconBackgroundFilled: "bg-stone-100 dark:bg-stone-950",
    iconBackgroundGradient: "bg-gradient-to-br from-stone-500 to-blue-500",
    iconBackgroundDefault: "bg-stone-50 dark:bg-stone-950/50",
    iconBorder: "border-stone-200 dark:border-stone-800",

    // Border classes
    borderDefault: "border-stone-200 dark:border-gray-700",
    borderLight: "border-stone-100 dark:border-gray-800",
    borderStrong: "border-stone-300 dark:border-gray-600",
    borderPrimary: "border-stone-700 dark:border-stone-500",
    borderSecondary: "border-stone-900 dark:border-stone-700",
    borderBottom: "border-b border-stone-200 dark:border-gray-700",
    borderAccent: "border-blue-600 dark:border-blue-400",

    // Badge classes
    badgeBackground: "bg-stone-100 dark:bg-stone-950",
    badgeText: "text-stone-700 dark:text-stone-300",

    // Shadow classes
    shadowPrimary: "shadow-stone-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-stone-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(120,113,108,0.30)]", // stone-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-stone-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-stone-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-stone-600 hover:bg-stone-700 dark:bg-stone-700 dark:hover:bg-stone-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-stone-300 dark:bg-stone-600",
    textEmphasis: "text-amber-600 dark:text-amber-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-stone-600 to-stone-700",
    gradientSecondary: "bg-gradient-to-br from-stone-700 to-stone-900",
    gradientAccent: "bg-gradient-to-r from-stone-500 to-blue-500",
    gradientSubtle:
      "bg-gradient-to-br from-stone-50 to-gray-100 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-stone-600 via-blue-500 to-stone-600",
    gradientHero: "bg-gradient-to-br from-stone-600 via-blue-500 to-stone-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
